/*
 * Operator-facing wording for a proposed change.
 *
 * Shared because two surfaces describe the same change and must agree: the card
 * in Ada's window, and the walkthrough that points at the control in the
 * console. If they drift, the tour highlights a switch the card just called
 * something else.
 *
 * Nothing here exposes a method, a path, or a raw field name — a proposal is
 * shown to an operator as a setting, never as a request.
 */

export const CHANGE_TITLES = {
  propose_blending: "Blending",
  propose_chaff: "Cover traffic",
  propose_timing_jitter: "Timing jitter",
  propose_wire_obfuscation: "Wire padding",
  propose_constant_rate_shaping: "Constant-rate shaping",
  propose_mtd_rotation: "Rotation",
  propose_anchor_balance: "Anchor balance",
  propose_anchor_weights: "Anchor weights",
  propose_chaff_shape: "Cover traffic shape",
  propose_constant_rate_shape: "Constant-rate shape",
  propose_class_action: "Routing policy",
  propose_class_wan_order: "Link preference",
  propose_class_wan_priority: "Staged link priority",
  propose_apply_wan_priority: "Apply staged priority",
  propose_reset_wan_priority: "Discard staged priority",
  propose_qos_thresholds: "Quality rules",
  propose_failover_thresholds: "Failover thresholds",
  propose_probing_config: "Health-check cadence",
  propose_throughput_probing: "Throughput measurement",
  propose_class_encryption: "Encryption",
  propose_encryption_all_classes: "Encryption, all classes",
  propose_aqm_rates: "Queue shaping",
  propose_clear_aqm: "Remove queue shaping",
  propose_cloud_switch: "Cloud anchor",
  propose_classifier_mode: "Traffic labelling",
  propose_wan_suppression: "Link suppression",
  propose_rotate_now: "Rotate now",
  propose_rotate_anchors_now: "Re-place flows now",
  propose_firewall: "Firewall",
  propose_watch_devices: "Watch where devices connect",
};

export const changeTitle = (tool) => CHANGE_TITLES[tool] || "Configuration change";

/* The panel's own words for the settings a proposal changes.
 *
 * Without this the fallback below is all there was, and it only strips
 * underscores — so a change came out as "period sec → 30, shape → constant,
 * spread → 0.3" while the operator was looking at a panel with "Pick a new rate
 * every", "Rate pattern" and a slider reading 40%. Three different names and
 * two different units for the same three settings. The reader is then asked to
 * work out that "spread 0.3" is the control marked 30%, which is not a thing to
 * ask of someone being walked through a feature for the first time.
 *
 * Only settings whose label has been read off the panel appear here. An
 * unmapped field falls through to the old wording, which is vague but never
 * wrong; a guessed label would be worse than the raw name.
 */
const pct = (v) => `${Math.round(Number(v) * 100)}%`;
const secs = (v) => `${v} second${Number(v) === 1 ? "" : "s"}`;

// ObfuscationPanel's PATTERNS and its blend segment, by their console labels.
const PATTERNS = {
  constant: "Steady", uniform: "Even spread", lognormal: "Natural",
  pareto: "Bursty", walk: "Drifting",
};
const SPLITS = { uniform: "Equal shares", weighted: "By capacity", off: "off" };

const named = (map) => (v) => map[v] || String(v);

// shape/spread/period are the same three controls on both shaping panels.
const SHAPE = {
  shape: ["Rate pattern", named(PATTERNS)],
  spread: ["How much it varies", pct],
  period_sec: ["Pick a new rate every", secs],
};

const FIELDS = {
  propose_chaff: { floor_pps: ["Keep the link at least", (v) => `${v} packets/sec`] },
  propose_chaff_shape: SHAPE,
  propose_constant_rate_shape: SHAPE,
  propose_timing_jitter: { max_ms: ["Delay each packet up to", (v) => `${v} ms`] },
  propose_blending: { mode: ["How the links share the load", named(SPLITS)] },
  propose_mtd_rotation: { period_sec: ["Re-key roughly every", secs] },
  propose_anchor_balance: { max_bias: ["Even out the volume", (v) => `${Number(v).toFixed(1)}×`] },
  propose_firewall: {
    on: ["Firewall", (v) => (v ? "on" : "off")],
    management_networks: ["Management networks", (v) => (v.length ? v.join(", ") : "none")],
  },
};

const fieldOf = (tool, k) => (tool && FIELDS[tool] && FIELDS[tool][k]) || null;

export const humanLabel = (k, tool) => {
  const f = fieldOf(tool, k);
  if (f) return f[0];
  return k.replace(/_/g, " ").replace(/\bms\b/, "(ms)").replace(/\bpps\b/, "(pps)")
          .replace(/^./, (c) => c.toUpperCase());
};

export const humanValue = (v, k, tool) => {
  if (v === true) return "on";
  if (v === false) return "off";
  if (v === "" || v === null || v === undefined) return "none";
  const f = fieldOf(tool, k);
  if (f) return f[1](v);
  if (Array.isArray(v)) return v.join(" → ");
  if (typeof v === "object") {
    return Object.entries(v).map(([kk, x]) => `${kk}: ${humanValue(x)}`).join(", ");
  }
  return String(v);
};

/* describeChange renders a proposal body as one readable clause.
 *
 * With a tool it speaks the panel's language and quotes each control the way it
 * is labelled on screen, so the sentence can be followed by reading, not by
 * translation. Without one it keeps the older field-name wording — which is
 * what the verification turn wants, because that text is read by the assistant
 * against its own API reading rather than by an operator against a panel. */
export const describeChange = (body, tool) => {
  const parts = Object.entries(body || {}).map(([k, v]) =>
    tool
      ? `“${humanLabel(k, tool)}” to ${humanValue(v, k, tool)}`
      : `${humanLabel(k).toLowerCase()} → ${humanValue(v)}`
  );
  if (!tool || parts.length < 2) return parts.join(", ");
  // An Oxford-less "a, b and c" reads as instructions; "a, b, c" reads as data.
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
};
